/**
 * UserPasswordChangePage
 * [UC-040] Password Change Use Case
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components/ui';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';
import { CheckCircle } from 'lucide-react';

interface PasswordChangeForm {
  currentPassword: string;
  newPassword: string;
  passwordConfirmation: string;
}

interface FormErrors {
  currentPassword?: string;
  newPassword?: string;
  passwordConfirmation?: string;
}

export function UserPasswordChangePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState<PasswordChangeForm>({
    currentPassword: '',
    newPassword: '',
    passwordConfirmation: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Current password validation
    if (!form.currentPassword) {
      newErrors.currentPassword = t('userPasswordChange.errors.currentRequired', 'Current password is required');
      isValid = false;
    }

    // New password validation
    if (!form.newPassword) {
      newErrors.newPassword = t('userPasswordChange.errors.newRequired', 'New password is required');
      isValid = false;
    } else if (form.newPassword.length < 8) {
      newErrors.newPassword = t('userPasswordChange.errors.passwordTooShort', 'New password must be at least 8 characters long');
      isValid = false;
    }

    // Password confirmation validation
    if (!form.passwordConfirmation) {
      newErrors.passwordConfirmation = t('userPasswordChange.errors.confirmRequired', 'Please confirm your new password');
      isValid = false;
    } else if (form.newPassword !== form.passwordConfirmation) {
      newErrors.passwordConfirmation = t('userPasswordChange.errors.passwordMismatch', 'New passwords do not match');
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await api.patch('/users/me/password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
        passwordConfirmation: form.passwordConfirmation
      });

      // Show success message
      setShowSuccessMessage(true);

      // Clear form
      setForm({
        currentPassword: '',
        newPassword: '',
        passwordConfirmation: ''
      });

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/user-dashboard');
      }, 2000);

    } catch (error: any) {
      setShowSuccessMessage(false);

      if (error.response?.status === 400) {
        const message = error.response.data?.detail || 'Validation failed';

        // Map specific error messages to fields
        if (message.includes('Current password is incorrect')) {
          setErrors({ currentPassword: t('userPasswordChange.errors.currentIncorrect', 'Current password is incorrect') });
        } else if (message.includes('New passwords do not match')) {
          setErrors({ passwordConfirmation: t('userPasswordChange.errors.passwordMismatch', 'New passwords do not match') });
        } else if (message.includes('must be at least 8 characters')) {
          setErrors({ newPassword: t('userPasswordChange.errors.passwordTooShort', 'New password must be at least 8 characters long') });
        } else {
          toast.error(message);
        }
      } else if (error.response?.status === 401) {
        toast.error(t('userPasswordChange.errors.unauthorized', 'Authentication required'));
        navigate('/user-login');
      } else if (error.response?.status === 404) {
        toast.error(t('userPasswordChange.errors.userNotFound', 'User not found'));
      } else {
        toast.error(t('userPasswordChange.errors.processingFailed', 'Password processing failed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Clear form and errors
    setForm({
      currentPassword: '',
      newPassword: '',
      passwordConfirmation: ''
    });
    setErrors({});
    setShowSuccessMessage(false);

    // Navigate back to dashboard
    navigate('/user-dashboard');
  };

  const handleInputChange = (field: keyof PasswordChangeForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
          {t('userPasswordChange.title', 'Change Password')}
        </h1>
        <p className="mt-1.5 text-base sm:text-sm text-gray-600">
          {t('userPasswordChange.subtitle', 'Update your account password to keep your account secure')}
        </p>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="mb-5 sm:mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-base sm:text-sm font-medium text-green-700">
              {t('userPasswordChange.successMessage', 'Password changed successfully')}
            </span>
          </div>
        </div>
      )}

      {/* Password Change Form */}
      <Card>
        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
          {/* Current Password Field */}
          <Input
            type="password"
            label={t('userPasswordChange.currentPasswordLabel', 'Current Password')}
            placeholder={t('userPasswordChange.currentPasswordPlaceholder', 'Enter your current password')}
            value={form.currentPassword}
            onChange={handleInputChange('currentPassword')}
            error={errors.currentPassword}
            disabled={isSubmitting}
          />

          {/* New Password Field */}
          <Input
            type="password"
            label={t('userPasswordChange.newPasswordLabel', 'New Password')}
            placeholder={t('userPasswordChange.newPasswordPlaceholder', 'Enter your new password')}
            value={form.newPassword}
            onChange={handleInputChange('newPassword')}
            error={errors.newPassword}
            disabled={isSubmitting}
          />

          {/* Confirm New Password Field */}
          <Input
            type="password"
            label={t('userPasswordChange.confirmLabel', 'Confirm New Password')}
            placeholder={t('userPasswordChange.confirmPlaceholder', 'Confirm your new password')}
            value={form.passwordConfirmation}
            onChange={handleInputChange('passwordConfirmation')}
            error={errors.passwordConfirmation}
            disabled={isSubmitting}
          />

          {/* Form Actions */}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting
                ? t('userPasswordChange.changing', 'Changing Password...')
                : t('userPasswordChange.changePassword', 'Change Password')
              }
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default UserPasswordChangePage;
